import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppstoreFilled,
  AppstoreOutlined,
  HomeFilled,
  HomeOutlined,
  EditOutlined,
  EditFilled,
  SettingOutlined,
  MailOutlined,
  GroupOutlined,
  DatabaseOutlined,
  RobotOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  BugOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  SecurityScanOutlined,
  PlayCircleOutlined,
  BuildOutlined,
  ApartmentOutlined,
  ExperimentOutlined,
  CloudServerOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import { ChevronDown, ChevronRight, WorkflowIcon } from "lucide-react";
import { SidebarNavItem } from "../ui/sidebar";
import { Separator } from "../ui/separator";
import { cn } from "../../lib/utils";
import useStore from "../../store/useStore";
import { usePermissions } from "../../PermissionsProvider";

type MenuItem = {
  key: string;
  icon?: ((selected: boolean) => React.ReactNode) | React.ReactNode;
  text: string;
  to?: string;
  disabled: boolean;
  needsTooltip: boolean;
  children?: MenuItem[];
};

const SiderComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const { dataReadyTutorial } = useStore();
  const { companyId } = usePermissions();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["build", "test-evaluate", "admin"])
  );

  const tooltipTitle = "Make a request using the Maitai SDK to view this page";

  const adminMenuItems: MenuItem[] = [
    {
      key: "annotation",
      text: "Annotation",
      to: "/admin/annotation",
      disabled: false,
      needsTooltip: false,
      icon: (selected: boolean) =>
        selected ? (
          <EditFilled className="w-4 h-4" />
        ) : (
          <EditOutlined className="w-4 h-4" />
        ),
    },

    {
      key: "email",
      text: "Email",
      to: "/admin/email",
      disabled: false,
      needsTooltip: false,
      icon: <MailOutlined className="w-4 h-4" />,
    },
    {
      key: "evaluations",
      text: "Evaluations",
      to: "/admin/evaluations",
      disabled: false,
      needsTooltip: false,
      icon: <CheckCircleOutlined className="w-4 h-4" />,
    },
    {
      key: "validation",
      text: "Validation",
      to: "/admin/validation/overview",
      disabled: false,
      needsTooltip: false,
      icon: <PlayCircleOutlined className="w-4 h-4" />,
    },
    {
      key: "ingest-data",
      text: "Ingest Data",
      to: "/admin/ingest-data",
      disabled: false,
      needsTooltip: false,
      icon: <UploadOutlined className="w-4 h-4" />,
    },
    {
      key: "agent-uptime-metrics",
      text: "Agent Uptime Metrics",
      to: "/admin/agent-uptime-metrics",
      disabled: false,
      needsTooltip: false,
      icon: <DashboardOutlined className="w-4 h-4" />,
    },
    {
      key: "instances",
      text: "Instances",
      to: "/admin/instances",
      disabled: false,
      needsTooltip: false,
      icon: <CloudServerOutlined className="w-4 h-4" />,
    },
    {
      key: "base-model-candidates",
      text: "Base Model Candidates",
      to: "/admin/base-model-candidates",
      disabled: false,
      needsTooltip: false,
      icon: <BuildOutlined className="w-4 h-4" />,
    },
  ];

  const buildMenuItems: MenuItem[] = [
    {
      key: "compositions",
      text: "Compositions",
      to: "/forge/compositions",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <DatabaseOutlined className="w-4 h-4" />,
    },
    {
      key: "finetuning",
      text: "Finetuning",
      to: "/forge/finetuning",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <ThunderboltOutlined className="w-4 h-4" />,
    },
    {
      key: "models-list",
      text: "Models",
      to: "/forge/models",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <RobotOutlined className="w-4 h-4" />,
    },
    {
      key: "agents",
      text: "Agents",
      to: "/forge/agents",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <GroupOutlined className="w-4 h-4" />,
    },
    {
      key: "workflows",
      text: "Workflows",
      to: "/forge/workflows",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <WorkflowIcon className="w-4 h-4" />,
    },
    {
      key: "conversation-trees",
      text: "Conversation Trees",
      to: "/forge/conversation-trees",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <ApartmentOutlined className="w-4 h-4" />,
    },
    {
      key: "studio",
      text: "Studio",
      to: "/forge/studio",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      icon: <ExperimentOutlined className="w-4 h-4" />,
    },
  ];

  const testEvaluateMenuItems: MenuItem[] = [
    {
      key: "test-sets",
      text: "Test Sets",
      to: "/test/test-sets",
      disabled: !dataReadyTutorial,
      needsTooltip: true,
      icon: <FileTextOutlined className="w-4 h-4" />,
    },
    {
      key: "criteria",
      text: "Test Criteria",
      to: "/test/criteria",
      disabled: !dataReadyTutorial,
      needsTooltip: true,
      icon: <CheckCircleOutlined className="w-4 h-4" />,
    },
    {
      key: "evaluations",
      text: "Evaluations",
      to: "/test/evaluations",
      disabled: !dataReadyTutorial,
      needsTooltip: true,
      icon: <SecurityScanOutlined className="w-4 h-4" />,
    },
  ];

  const menuItems: MenuItem[] = [
    {
      key: "home",
      icon: (selected: boolean) =>
        selected ? (
          <HomeFilled className="w-5 h-5" />
        ) : (
          <HomeOutlined className="w-5 h-5" />
        ),
      text: "Home",
      to: "/",
      disabled: false,
      needsTooltip: false,
    },
    {
      key: "applications",
      icon: (selected: boolean) =>
        selected ? (
          <AppstoreFilled className="w-5 h-5" />
        ) : (
          <AppstoreOutlined className="w-5 h-5" />
        ),
      text: "Applications",
      to: "/applications",
      disabled: !dataReadyTutorial,
      needsTooltip: true,
    },
    {
      key: "intent-groups",
      icon: (selected: boolean) =>
        selected ? (
          <GroupOutlined className="w-5 h-5" />
        ) : (
          <GroupOutlined className="w-5 h-5" />
        ),
      text: "Intent Groups",
      to: "/intent-groups",
      disabled: !dataReadyTutorial,
      needsTooltip: true,
    },
    {
      key: "forge",
      icon: <BuildOutlined className="w-5 h-5" />,
      text: "Forge",
      to: "/forge",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      children: buildMenuItems,
    },
    {
      key: "test-evaluate",
      icon: <BugOutlined className="w-5 h-5" />,
      text: "Test & Evaluate",
      disabled: !dataReadyTutorial,
      needsTooltip: false,
      children: testEvaluateMenuItems,
    },
  ];

  if (companyId === 2) {
    menuItems.splice(6, 0, {
      key: "admin",
      text: "Admin",
      to: "/admin",
      disabled: false,
      icon: <SettingOutlined className="w-5 h-5" />,
      needsTooltip: false,
      children: adminMenuItems,
    });
  }

  const isItemActive = (item: MenuItem): boolean => {
    if (item.to && pathname === item.to) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => child.to === pathname);
    }
    return false;
  };

  const toggleSection = (key: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSections(newExpanded);
  };

  const handleNavigation = (to: string, disabled: boolean) => {
    if (!disabled) {
      navigate(to);
    }
  };

  const renderMenuItem = (item: MenuItem, isSubItem: boolean = false) => {
    const isActive = isItemActive(item);
    const isExpanded = expandedSections.has(item.key);

    // Handle items with children (collapsible sections)
    if (item.children) {
      // Special handling for Build section - always expanded, clickable
      if (item.key === "forge") {
        return (
          <div key={item.key} className="space-y-1">
            <button
              onClick={() => handleNavigation(item.to!, item.disabled)}
              disabled={item.disabled}
              className={cn(
                "w-full flex items-center px-6 py-2.5 text-sm rounded-md transition-colors",
                "hover:bg-accent hover:text-accent-foreground font-medium",
                isActive && "bg-accent text-accent-foreground",
                item.disabled && "opacity-50 cursor-not-allowed"
              )}>
              <div className="flex items-center gap-3">
                {typeof item.icon === "function"
                  ? item.icon(isActive)
                  : item.icon}
                <span>{item.text}</span>
              </div>
            </button>

            {/* Always show children for Build */}
            <div className="ml-6 space-y-1">
              {item.children.map((child) => renderMenuItem(child, true))}
            </div>
          </div>
        );
      }

      // Regular collapsible sections for other items
      return (
        <div key={item.key} className="space-y-1">
          <button
            onClick={() => toggleSection(item.key)}
            disabled={item.disabled}
            className={cn(
              "w-full flex items-center justify-between px-6 py-2.5 text-sm rounded-md transition-colors",
              "hover:bg-accent hover:text-accent-foreground font-medium",
              isActive && "bg-accent text-accent-foreground",
              item.disabled && "opacity-50 cursor-not-allowed"
            )}>
            <div className="flex items-center gap-3">
              {typeof item.icon === "function"
                ? item.icon(isActive)
                : item.icon}
              <span>{item.text}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {isExpanded && (
            <div className="ml-6 space-y-1">
              {item.children.map((child) => renderMenuItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    // Handle divider items
    if (!item.to) {
      return (
        <div key={item.key} className="px-6 py-2">
          <Separator className="my-2" />
          <div className="flex items-center gap-3 py-2 text-sm text-muted-foreground">
            {typeof item.icon === "function" ? item.icon(false) : item.icon}
            <span>{item.text}</span>
          </div>
        </div>
      );
    }

    // Handle regular menu items
    return (
      <SidebarNavItem
        key={item.key}
        active={isActive}
        onClick={() => handleNavigation(item.to!, item.disabled)}
        className={cn(
          isSubItem && "text-sm py-2 ml-6",
          item.disabled && "opacity-50 cursor-not-allowed"
        )}>
        <div className="flex items-center gap-3">
          {typeof item.icon === "function" ? item.icon(isActive) : item.icon}
          <span>{item.text}</span>
        </div>
      </SidebarNavItem>
    );
  };

  return (
    <nav className="space-y-1">
      {menuItems.map((item, index) => (
        <React.Fragment key={item.key}>
          {item.key === "more" && <Separator className="my-2" />}
          {renderMenuItem(item)}
        </React.Fragment>
      ))}
    </nav>
  );
};

export default SiderComponent;
